import { SupplierFormFields } from "@/components/suppliers/SupplierFormFields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  createSupplier,
  emptySupplierDraft,
  isSupplierDraftComplete,
  normalizeSupplierDraft,
  supplierErrorMessage,
  validateSupplierDraft,
  type SupplierDraft,
  type SupplierRecord,
} from "@/lib/suppliers/suppliersStore";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: SupplierRecord) => void | Promise<void>;
  title?: string;
};

export function CreateSupplierDialog({
  open,
  onOpenChange,
  onCreated,
  title = "Add supplier",
}: Props) {
  const [draft, setDraft] = useState<SupplierDraft>(() => emptySupplierDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptySupplierDraft());
    setSaving(false);
  }, [open]);

  const save = async () => {
    if (saving) return;
    const validation = validateSupplierDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required supplier fields.");
      return;
    }
    const payload = normalizeSupplierDraft(draft);
    setSaving(true);
    try {
      const { record, message } = await createSupplier(payload);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(supplierErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[min(96vw,960px)] overflow-y-auto sm:max-w-[960px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <SupplierFormFields draft={draft} onChange={setDraft} disabled={saving} />
        <DialogFooter>
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="premium"
            onClick={() => void save()}
            disabled={saving || !isSupplierDraftComplete(draft)}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save supplier"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
